import type { DnsechoRow, DohResolver, DohResolverMeta, DohServer, IpDetails, ShareRow } from '@shared/api'

/**
 * Same-origin throughout: nginx proxies /api to the apiserver, and the Vite dev
 * server mirrors that. No base URL to configure or get wrong.
 */
async function getJson<T>(path: string, signal?: AbortSignal): Promise<T> {
    const res = await fetch(path, { signal, headers: { accept: 'application/json' } })
    if (!res.ok) throw new Error(`${path} -> HTTP ${res.status}`)
    return res.json() as Promise<T>
}

async function postJson<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
    const res = await fetch(path, {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        body: JSON.stringify(body),
        signal,
    })
    if (!res.ok) throw new Error(`${path} -> HTTP ${res.status}`)
    return res.json() as Promise<T>
}

/** Every endpoint answers with a retcode; 200 is the only success value. */
function unwrap<T extends { retcode: number }>(payload: T, path: string): T {
    if (payload.retcode !== 200) throw new Error(`${path} -> retcode ${payload.retcode}`)
    return payload
}

//#region identity
export interface IpResponse extends IpDetails {
    retcode: number
    ip: string
    headers?: Record<string, string>
    userAgent?: Record<string, unknown>
}

export const fetchSelf = (signal?: AbortSignal) =>
    getJson<IpResponse>('/api/ip', signal).then((r) => unwrap(r, '/api/ip'))

export const fetchRdns = (ip: string, signal?: AbortSignal) =>
    getJson<{ retcode: number; data?: string[] }>(`/api/ip/rdns/${ip}`, signal)
        // -1 simply means "no PTR record", which is normal rather than an error
        .then((r) => (r.retcode === 200 ? r.data?.[0] : undefined))
//#endregion

//#region leak test
export const fetchDnsecho = (identifier: string, signal?: AbortSignal) =>
    getJson<{ retcode: number; data?: DnsechoRow[] }>(`/api/dnsecho/check/${identifier}`, signal)
        // -100 means the authoritative server has not seen this token yet, which
        // is the normal state for the first few polls
        .then((r) => (r.retcode === 200 ? (r.data ?? []) : []))

export interface ShareCreated { retcode: number; id: string; createdDate: number }

export const createShare = (rows: ShareRow[], hostName: string) =>
    postJson<ShareCreated>('/api/dns-share', { data: JSON.stringify(rows), hostName })
        .then((r) => unwrap(r, '/api/dns-share'))

export const fetchShare = (id: string, signal?: AbortSignal) =>
    getJson<{ retcode: number; data?: { user: Record<string, unknown>; leaktest: ShareRow[] } }>(
        `/api/dns-shares/${id}.json`, signal,
    ).then((r) => (r.retcode === 200 ? r.data : undefined))
//#endregion

//#region doh
export const fetchDohServers = (signal?: AbortSignal) =>
    getJson<{ retcode: number; servers?: DohServer[] }>('/api/doh/servers', signal)
        .then((r) => unwrap(r, '/api/doh/servers').servers ?? [])

/** The whole validated resolver pool. Deliberately unfiltered and unpaged: one
    url means one cache entry, and the page filters the set locally. */
export interface DohResolverPayload {
    resolvers: DohResolver[]
    meta: DohResolverMeta
}

export const fetchDohResolvers = (signal?: AbortSignal): Promise<DohResolverPayload> =>
    getJson<{ retcode: number; resolvers?: DohResolver[]; meta?: DohResolverMeta }>(
        '/api/doh/resolvers', signal,
    ).then((r) => {
        const payload = unwrap(r, '/api/doh/resolvers')
        return {
            resolvers: payload.resolvers ?? [],
            meta: payload.meta ?? { size: 0, lastRefreshed: 0, generatedAt: Date.now() },
        }
    })

export const saveDohSpeed = (rows: { name: string; url: string; latency: number }[]) =>
    postJson<{ retcode: number }>('/api/doh/speed/save', { data: JSON.stringify(rows) })

export const saveCustomDoh = (name: string, url: string) =>
    postJson<{ retcode: number }>('/api/doh/custom/save', { data: JSON.stringify({ name, url }) })
//#endregion
